import { BaseContentfulEntry } from '@components/renderer/types'
import { NinetailedSsrPlugin, ExperienceConfiguration } from '@ninetailed/experience.js-next'
import { ComponentTypeEnum } from '@ninetailed/experience.js-shared'
import { NinetailedInsightsPlugin } from '@ninetailed/experience.js-plugin-insights'
import { NinetailedPlugin } from '@ninetailed/experience.js-plugin-analytics'
import { FacadeService, ProfileExperience } from '@lib/services/facade'
import { NinetailedPreviewPluginData } from '@lib/api/ninetailed'

type Plugins = NinetailedPlugin[]

export interface Variant {
  variant: BaseContentfulEntry
  index: number
}

export interface NinetailedPersonalizationService {
  isNinetailedEnabled(): boolean

  /**
   * Returns personalized variant if conditions are met, otherwise returns the original structure
   */
  resolveVariant(structure: BaseContentfulEntry, parsedExperiences: ExperienceConfiguration[]): Variant

  /**
   * Returns true if personalization should be rendered using library renderer that Ninetailed provides
   */
  shouldUseLibraryRenderer(): boolean

  getPlugins(): Plugins

  getPluginsForPreview(ninetailedPreviewPluginData?: NinetailedPreviewPluginData): Promise<Plugins>

  /**
   * Preview mode show panel with experiences and audiences
   */
  setPreviewMode(isPreviewMode: boolean): void
}

export class NinetailedPersonalizationServiceImpl implements NinetailedPersonalizationService {
  private readonly profileExperiences: ProfileExperience[]
  private isPreviewMode = false
  private isPersonalizationEnabled = false

  constructor(private readonly facadeService: FacadeService) {
    this.profileExperiences = (this.facadeService.pick('profileExperiences') as ProfileExperience[]) || []
    this.isPersonalizationEnabled = this.profileExperiences.length > 0
  }

  isNinetailedEnabled(): boolean {
    return this.isPreviewMode || this.isPersonalizationEnabled
  }

  resolveVariant(structure: BaseContentfulEntry, parsedExperiences: ExperienceConfiguration[]): Variant {
    const matchingExperience = this.findMatchingExperience(parsedExperiences, this.profileExperiences)

    if (!matchingExperience) {
      return { variant: structure, index: 0 }
    }

    const variant = this.findVariant(matchingExperience, structure, this.profileExperiences)
    return variant
  }

  shouldUseLibraryRenderer(): boolean {
    if (this.isPreviewMode) {
      return true
    }

    return false
  }

  getPlugins(): Plugins {
    if (this.shouldUseLibraryRenderer()) {
      return []
    }

    return [new NinetailedSsrPlugin(), new NinetailedInsightsPlugin()]
  }

  async getPluginsForPreview(ninetailedPreviewPluginData?: NinetailedPreviewPluginData): Promise<Plugins> {
    const { default: NinetailedPreviewPlugin } = await import('@ninetailed/experience.js-plugin-preview')
    return [
      new NinetailedPreviewPlugin({
        experiences: ninetailedPreviewPluginData?.experiences ?? [],
        audiences: ninetailedPreviewPluginData?.audiences ?? [],
        ui: { opener: { hide: false } },
      }),
    ]
  }

  setPreviewMode(isPreviewMode: boolean): void {
    this.isPreviewMode = isPreviewMode
  }

  private findMatchingExperience(
    parsedExperiences: ExperienceConfiguration[],
    profileExperiences: ProfileExperience[]
  ): ExperienceConfiguration | undefined {
    return parsedExperiences.find((exp) => profileExperiences.some((decision) => decision.experienceId === exp.id))
  }

  private findVariant(
    matchingExperience: ExperienceConfiguration,
    structure: BaseContentfulEntry,
    profileExperiences: ProfileExperience[]
  ): { variant: BaseContentfulEntry; index: number } {
    const matchingDecision = profileExperiences.find(({ experienceId }) => experienceId === matchingExperience.id)

    const index = matchingDecision?.variantIndex || 0
    const matchingComponent = matchingExperience.components.find(
      ({ baseline, type }) => type === ComponentTypeEnum.EntryReplacement && baseline.id === structure.sys.id
    )
    const selectableVariants = [structure, ...(matchingComponent?.variants || [])]
    const variant = selectableVariants[index] as BaseContentfulEntry

    return { variant, index }
  }
}
